// src/features/portfolio/portfolioSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../api/apiClient';

// Fetch portfolios for the logged-in user
export const fetchPortfolios = createAsyncThunk('portfolio/fetchPortfolios', async (_, thunkAPI) => {
  try {
    const response = await apiClient.get('/portfolios');
    return response.data?.portfolios || [];
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch portfolios');
  }
});

export const fetchPortfolioById = createAsyncThunk(
  'portfolio/fetchPortfolioById',
  async (id, thunkAPI) => {
    try {
      const response = await apiClient.get(`/portfolios/${id}`);
      return response.data?.portfolio || null;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch portfolio');
    }
  }
);

export const createPortfolio = createAsyncThunk(
  'portfolio/createPortfolio',
  async ({ name, description, baseCurrency }, thunkAPI) => {
    try {
      const response = await apiClient.post('/portfolios', { name, description, baseCurrency });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to create portfolio');
    }
  }
);

export const fetchSnapshotsByPortfolio = createAsyncThunk(
  'portfolio/fetchSnapshotsByPortfolio',
  async (portfolioId, thunkAPI) => {
    try {
      const response = await apiClient.get(`/portfolios/${portfolioId}/snapshots`);
      return { portfolioId, snapshots: response.data?.snapshots || [] };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch snapshots');
    }
  }
);

// Async thunk to add a new snapshot
export const addSnapshot = createAsyncThunk(
  'portfolio/addSnapshot',
  async (snapshotData, { rejectWithValue }) => {
    try {
      const { portfolioId, date, value } = snapshotData;
      const response = await apiClient.post(
        `/portfolios/${portfolioId}/snapshots`,
        { date, value },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

// Thunk to delete a snapshot by ID
export const deleteSnapshot = createAsyncThunk(
  'portfolio/deleteSnapshot',
  async ({ portfolioId, snapshotId }, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/portfolios/${portfolioId}/snapshots/${snapshotId}`);
      return { portfolioId, snapshotId }; // Return both values so we can update the state
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete snapshot');
    }
  }
);

export const fetchAssetsByPortfolio = createAsyncThunk(
  'portfolio/fetchAssetsByPortfolio',
  async ({ portfolioId, nextPageToken = null, limit = 10 }, thunkAPI) => {
    try {
      const response = await apiClient.get(`/portfolios/${portfolioId}/assets`, {
        params: { limit, nextPageToken },
      });

      return {
        portfolioId,
        assets: response.data.assets,
        nextPageToken: response.data.nextPageToken || null, // Store next page token
        total: response.data.total,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch assets');
    }
  }
);

export const fetchEnrichedHoldings = createAsyncThunk(
  'portfolio/fetchEnrichedHoldings',
  async (portfolioId, thunkAPI) => {
    try {
      const response = await apiClient.get(`/portfolios/${portfolioId}/holdings/details`);
      return { portfolioId, holdings: response.data };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch enriched holdings');
    }
  }
);

export const addHoldingToPortfolio = createAsyncThunk(
  'portfolio/addHoldingToPortfolio',
  async ({ portfolioId, assetId, quantity, averagePrice }, thunkAPI) => {
    try {
      const response = await apiClient.post(`/portfolios/${portfolioId}/holdings`, {
        assetId,
        quantity,
        averagePrice,
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to add holding');
    }
  }
);

export const deleteHoldingFromPortfolio = createAsyncThunk(
  'portfolio/deleteHoldingFromPortfolio',
  async ({ portfolioId, holdingId }, thunkAPI) => {
    try {
      await apiClient.delete(`/portfolios/${portfolioId}/holdings/${holdingId}`);
      return { portfolioId, holdingId };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to delete holding');
    }
  }
);

export const updateHoldingInPortfolio = createAsyncThunk(
  'portfolio/updateHoldingInPortfolio',
  async ({ portfolioId, assetId, quantity, averagePrice }, thunkAPI) => {
    try {
      const response = await apiClient.put(`/portfolios/${portfolioId}/holdings/${assetId}`, {
        quantity,
        averagePrice,
      });

      return { portfolioId, assetId, updatedHolding: response.data };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to update holding');
    }
  }
);

const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState: {
    portfolios: [],
    selectedPortfolio: null,
    snapshots: {},
    assets: {},
    assetPagination: {},
    enrichedHoldings: {},
    fetchSnapshotStatus: 'idle',
    addSnapshotStatus: 'idle',
    deleteSnapshotStatus: 'idle',
    fetchAssetsStatus: 'idle',
    listPortfoliosStatus: 'idle',
    fetchPortfolioStatus: 'idle',
    createPortfolioStatus: 'idle',
    fetchEnrichedHoldingsStatus: 'idle',
    deleteHoldingStatus: 'idle',
    updateHoldingStatus: 'idle',
    error: null,
  },
  reducers: {
    resetFetchSnapshotStatus: (state) => {
      state.fetchSnapshotStatus = 'idle';
      state.error = null;
    },
    resetAddSnapshotStatus: (state) => {
      state.addSnapshotStatus = 'idle';
      state.error = null;
    },
    resetDeleteSnapshotStatus: (state) => {
      state.deleteSnapshotStatus = 'idle';
      state.error = null;
    },
    resetFetchAssetsStatus: (state) => {
      state.fetchAssetsStatus = 'idle';
      state.error = null;
    },
    goToPreviousAssetPage: (state, action) => {
      console.log('goToPreviousAssetPage', action.payload);
      const { portfolioId } = action.payload;
      if (state.assetPagination[portfolioId]?.prevTokens?.length > 1) {
        state.assetPagination[portfolioId].prevTokens.pop(); // Remove last token
        state.assetPagination[portfolioId].currentPageToken =
          state.assetPagination[portfolioId].prevTokens[state.assetPagination[portfolioId].prevTokens.length - 1] || null;
      }

      console.log(state.assetPagination[portfolioId].currentPageToken);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSnapshotsByPortfolio.pending, (state) => {
        state.fetchSnapshotStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchSnapshotsByPortfolio.fulfilled, (state, action) => {
        const { portfolioId, snapshots } = action.payload;
        state.fetchSnapshotStatus = 'succeeded';
        //state.snapshots[portfolioId] = snapshots; // Store snapshots by portfolio ID
        state.snapshots[portfolioId] = snapshots.sort((a, b) => new Date(a.date) - new Date(b.date));
      })
      .addCase(fetchSnapshotsByPortfolio.rejected, (state, action) => {
        state.fetchSnapshotStatus = 'failed';
        state.error = action.payload;
      })

      .addCase(addSnapshot.pending, (state) => {
        state.addSnapshotStatus = 'loading';
        state.error = null;
      })
      .addCase(addSnapshot.fulfilled, (state, action) => {
        const snapshot = action.payload;
        state.addSnapshotStatus = 'succeeded';

        if (!state.snapshots[snapshot.portfolioId]) {
          state.snapshots[snapshot.portfolioId] = []; // Initialize as an empty array if missing
        }

        state.snapshots[snapshot.portfolioId].push(snapshot);
        state.snapshots[snapshot.portfolioId].sort((a, b) => new Date(a.date) - new Date(b.date));
      })
      .addCase(addSnapshot.rejected, (state, action) => {
        state.addSnapshotStatus = 'failed';
        state.error = action.payload.error || 'Failed to add snapshot';
      })

      .addCase(deleteSnapshot.pending, (state) => {
        state.deleteStatus = 'loading';
        state.error = null;
      })
      .addCase(deleteSnapshot.fulfilled, (state, action) => {
        const { portfolioId, snapshotId } = action.payload;
      
        if (state.snapshots[portfolioId]) {
          state.snapshots[portfolioId] = state.snapshots[portfolioId].filter(
            (snapshot) => snapshot._id !== snapshotId
          );
        }
      
        state.deleteSnapshotStatus = 'succeeded';
      })
      .addCase(deleteSnapshot.rejected, (state, action) => {
        state.deleteSnapshotStatus = 'failed';
        state.error = action.payload.error || 'Failed to delete snapshot';
      })

      .addCase(fetchPortfolios.pending, (state) => {
        state.listPortfoliosStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchPortfolios.fulfilled, (state, action) => {
        state.listPortfoliosStatus = 'succeeded';
        state.portfolios = action.payload;
      })
      .addCase(fetchPortfolios.rejected, (state, action) => {
        state.listPortfoliosStatus = 'failed';
        state.error = action.payload;
      })

      .addCase(createPortfolio.pending, (state) => {
        state.createPortfolioStatus = 'loading';
        state.error = null;
      })
      .addCase(createPortfolio.fulfilled, (state, action) => {
        state.createPortfolioStatus = 'succeeded';
        state.portfolios.push(action.payload.portfolio); // Add the new portfolio to the list
      })
      .addCase(createPortfolio.rejected, (state, action) => {
        state.createPortfolioStatus = 'failed';
        state.error = action.payload;
      })

      .addCase(fetchPortfolioById.pending, (state) => {
        state.fetchPortfolioStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchPortfolioById.fulfilled, (state, action) => {
        state.fetchPortfolioStatus = 'succeeded';
        state.selectedPortfolio = action.payload;
      })
      .addCase(fetchPortfolioById.rejected, (state, action) => {
        state.fetchPortfolioStatus = 'failed';
        state.error = action.payload;
      })

      .addCase(fetchAssetsByPortfolio.pending, (state) => {
        state.fetchAssetsStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchAssetsByPortfolio.fulfilled, (state, action) => {
        const { portfolioId, assets, nextPageToken } = action.payload;
      
        if (!state.assetPagination[portfolioId]) {
          state.assetPagination[portfolioId] = { prevTokens: [] }; // Initialize pagination state
        }
      
        // ✅ Always store the last `currentPageToken` before updating it
        if (state.assetPagination[portfolioId].currentPageToken !== null) {
          state.assetPagination[portfolioId].prevTokens.push(state.assetPagination[portfolioId].currentPageToken);
        }
      
        // Update assets and tokens
        state.assets[portfolioId] = assets;
        state.assetPagination[portfolioId].currentPageToken = nextPageToken || null;
        state.assetPagination[portfolioId].nextPageToken = nextPageToken || null;
        state.fetchAssetsStatus = 'succeeded';
      })
      .addCase(fetchAssetsByPortfolio.rejected, (state, action) => {
        state.fetchAssetsStatus = 'failed';
        state.error = action.payload;
      })

      .addCase(fetchEnrichedHoldings.pending, (state) => {
        state.fetchEnrichedHoldingsStatus = 'loading';
      })
      .addCase(fetchEnrichedHoldings.fulfilled, (state, action) => {
        const { portfolioId, holdings } = action.payload;
        state.enrichedHoldings[portfolioId] = holdings; // Store by portfolio ID
        state.fetchEnrichedHoldingsStatus = 'succeeded';
      })
      .addCase(fetchEnrichedHoldings.rejected, (state, action) => {
        state.fetchEnrichedHoldingsStatus = 'failed';
        state.error = action.payload || 'Failed to fetch enriched holdings';
      })

      .addCase(addHoldingToPortfolio.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(addHoldingToPortfolio.fulfilled, (state) => {
        state.status = 'succeeded';
        // Optionally update portfolio holdings locally if needed
      })
      .addCase(addHoldingToPortfolio.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to add holding';
      })

      .addCase(deleteHoldingFromPortfolio.pending, (state) => {
        state.deleteHoldingStatus = 'loading';
      })
      .addCase(deleteHoldingFromPortfolio.fulfilled, (state, action) => {
        const { portfolioId, holdingId } = action.payload;
        const holdings = state.enrichedHoldings[portfolioId] || [];
        state.enrichedHoldings[portfolioId] = holdings.filter((holding) => holding._id !== holdingId);
        state.deleteHoldingStatus = 'succeeded';
      })
      .addCase(deleteHoldingFromPortfolio.rejected, (state, action) => {
        state.deleteHoldingStatus = 'failed';
        state.error = action.payload || 'Failed to delete holding';
      })

      .addCase(updateHoldingInPortfolio.pending, (state) => {
        state.updateHoldingStatus = 'loading';
      })
      .addCase(updateHoldingInPortfolio.fulfilled, (state, action) => {
        const { assetId, updatedHolding } = action.payload;
      
        if (state.selectedPortfolio?.holdings) {
          state.selectedPortfolio.holdings = state.selectedPortfolio.holdings.map((holding) =>
            holding.assetId === assetId ? { ...holding, ...updatedHolding } : holding
          );
        }
      
        state.updateHoldingStatus = 'succeeded';
      })
      .addCase(updateHoldingInPortfolio.rejected, (state, action) => {
        state.updateHoldingStatus = 'failed';
        state.error = action.payload || 'Failed to update holding';
      });
  },
});

export const { 
  resetFetchSnapshotStatus, 
  resetAddSnapshotStatus, 
  resetDeleteSnapshotStatus, 
  resetFetchAssetsStatus,
  listPortfoliosStatus,
  createPortfolioStatus,
  fetchAssetsStatus,
  goToPreviousAssetPage,
} = portfolioSlice.actions;
export default portfolioSlice.reducer;
